import { useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  ChefHat,
  PackageOpen,
  Utensils,
} from 'lucide-react';

import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../../components/DataTable/BasicPagination';
import SectionWrapper from '../../../components/SectionWrapper/SectionWrapper';
import useRestaurantRecipes from '../../../hooks/restaurant/useRestaurantRecipes';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import BranchService from '../../../services/branch/BranchService';
import RestaurantMenuCategoryService from '../../../services/restaurant/RestaurantMenuCategoryService';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';
import { IBranchDropdown } from '../../../types/branches/branchInterfaces';
import { RestaurantMenuCategoryDTO } from '../../../types/restaurant/RestaurantMenuTypes';
import {
  RestaurantPageRequest,
  RestaurantRecipeFilters,
} from '../../../types/restaurant/RestaurantRecipeTypes';

import RestaurantRecipeFormModal from './RestaurantRecipeFormModal';
import RecipeCard from './components/RecipeCard';
import RecipeTable from './components/RecipeTable';
import RecipeFilters, {
  RecipeActiveFilter,
} from './components/RecipeFilters';
import RecipeHeader from './components/RecipeHeader';

const RestaurantRecipesScreen = () => {
  // =====================================================
  // 🔹 Sucursales
  // =====================================================

  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);

  // =====================================================
  // 🔹 Filtros
  // =====================================================

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(0);
  const [categories, setCategories] = useState<RestaurantMenuCategoryDTO[]>([]);
  const [menuItemId, setMenuItemId] = useState(0);
  const [activeFilter, setActiveFilter] =
    useState<RecipeActiveFilter>('ACTIVE');

  // =====================================================
  // 🔹 Paginación
  // =====================================================

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [sort] = useState(['name,asc', 'id,asc']);

  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const debouncedSearch = useDebouncedValue(search, 350);
  const recipes = useRestaurantRecipes(branchId);

  // =====================================================
  // 🔹 Cargar sucursales
  // =====================================================

  useEffect(() => {
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((items) => {
        setBranches(items);

        if (items.length > 0) {
          setBranchId(items[0].id);
        }
      })
      .finally(() => setLoadingBranches(false));
  }, [canPickBranch]);

  // =====================================================
  // 🔹 Cargar opciones
  // =====================================================

  useEffect(() => {
    if (branchId) {
      recipes.loadOptions();
      RestaurantMenuCategoryService.listFiltered(branchId, {
        search: null,
        active: true,
        page: 0,
        size: 50,
      }).then((response) => setCategories(response.content));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  // =====================================================
  // 🔹 Construcción de petición
  // =====================================================

  const request = useMemo<
    RestaurantPageRequest<RestaurantRecipeFilters>
  >(
    () => ({
      page,
      size,
      sort,
      filters: {
        search: debouncedSearch.trim() || null,
        categoryId: categoryId || null,
        menuItemId: menuItemId || null,
        active:
          activeFilter === 'ALL'
            ? null
            : activeFilter === 'ACTIVE',
      },
    }),
    [
      activeFilter,
      categoryId,
      debouncedSearch,
      menuItemId,
      page,
      size,
      sort,
    ]
  );

  // =====================================================
  // 🔹 Cargar listado
  // =====================================================

  useEffect(() => {
    if (branchId) {
      recipes.load(request);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, request]);

  // =====================================================
  // 🔹 Navegación
  // =====================================================

  const previousPage = () => {
    setPage((current) => Math.max(0, current - 1));
  };

  // =====================================================
  // 🔹 Reiniciar filtros
  // =====================================================

  const resetFilters = () => {
    setSearch('');
    setCategoryId(0);
    setMenuItemId(0);
    setActiveFilter('ACTIVE');
    setPage(0);
  };

  // =====================================================
  // 🔹 Eventos del header
  // =====================================================

  const handleBranchChange = (nextBranchId: number) => {
    resetFilters();
    setBranchId(nextBranchId);
  };

  // =====================================================
  // 🔹 Eventos de filtros
  // =====================================================

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleMenuItemChange = (value: number) => {
    setMenuItemId(value);
    setPage(0);
  };

  const handleCategoryChange = (value: number) => {
    setCategoryId(value);
    setPage(0);
  };

  const handleActiveChange = (
    value: RecipeActiveFilter
  ) => {
    setActiveFilter(value);
    setPage(0);
  };

  // =====================================================
  // 🔹 Datos del listado
  // =====================================================

  const items = recipes.page?.content ?? [];

  // =====================================================
  // 🔹 Estadísticas de página
  // =====================================================

  const ingredientsInPage = items.reduce(
    (total, item) => total + item.ingredientCount,
    0
  );

  const menuItemsWithRecipeCount = new Set(
    items.map((item) => item.menuItemId)
  ).size;

  // =====================================================
  // 🔹 Validaciones
  // =====================================================

  // Ya no exige ítems de menú cargados: se pueden crear al vuelo desde el modal.
  const createDisabled =
    !branchId ||
    recipes.loadingOptions ||
    recipes.rawMaterials.length === 0 ||
    recipes.units.length === 0;

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        {/* =====================================================
            🔹 Header
        ===================================================== */}

        <RecipeHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          canPickBranch={canPickBranch}
          createDisabled={createDisabled}
          onBranchChange={handleBranchChange}
          onCreate={recipes.openCreate}
        />

        {/* =====================================================
            🔹 Resumen
        ===================================================== */}

        <div className="grid grid-cols-1 gap-3 border-b border-stroke bg-gray-2/40 p-5 dark:border-strokedark dark:bg-boxdark-2/30 sm:grid-cols-3 sm:px-7">
          <div className="rounded-xl border border-stroke bg-white p-4 shadow-card-2 dark:border-strokedark dark:bg-boxdark dark:shadow-none">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-bodydark2">
              <BookOpen className="h-4 w-4 text-primary" />
              Recetas encontradas
            </p>

            <p className="mt-1 text-2xl font-semibold text-black dark:text-white">
              {recipes.page?.page.totalElements ?? 0}
            </p>
          </div>

          <div className="rounded-xl border border-stroke bg-white p-4 shadow-card-2 dark:border-strokedark dark:bg-boxdark dark:shadow-none">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-bodydark2">
              <Utensils className="h-4 w-4 text-success" />
              Ingredientes en página
            </p>

            <p className="mt-1 text-2xl font-semibold text-success">
              {ingredientsInPage}
            </p>
          </div>

          <div className="rounded-xl border border-stroke bg-white p-4 shadow-card-2 dark:border-strokedark dark:bg-boxdark dark:shadow-none">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-bodydark2">
              <PackageOpen className="h-4 w-4 text-warning" />
              Ítems con receta
            </p>

            <p className="mt-1 text-2xl font-semibold text-black dark:text-white">
              {menuItemsWithRecipeCount}
            </p>
          </div>
        </div>

        {/* =====================================================
            🔹 Filtros
        ===================================================== */}

        <RecipeFilters
          search={search}
          categoryId={categoryId}
          menuItemId={menuItemId}
          activeFilter={activeFilter}
          menuItems={recipes.menuItems}
          categories={categories}
          onSearchChange={handleSearchChange}
          onCategoryChange={handleCategoryChange}
          onMenuItemChange={handleMenuItemChange}
          onActiveChange={handleActiveChange}
        />

        {/* =====================================================
            🔹 Cargando
        ===================================================== */}

        {recipes.loading ? (
          <div className="grid grid-cols-1 gap-4 p-5 sm:px-7 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="h-72 animate-pulse rounded-xl border border-stroke bg-gray-2 dark:border-strokedark dark:bg-meta-4"
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          /* =====================================================
              🔹 Estado vacío
          ===================================================== */

          <div className="flex min-h-[320px] flex-col items-center justify-center px-5 text-center sm:px-7">
            <span className="rounded-xl border border-primary/15 bg-primary/10 p-3 text-primary dark:border-primary/25 dark:bg-primary/15">
              <ChefHat className="h-8 w-8" />
            </span>

            <h3 className="mt-4 font-semibold text-black dark:text-white">
              No hay recetas para mostrar
            </h3>

            <p className="mt-1 max-w-md text-sm text-bodydark2">
              Cambie los filtros seleccionados o registre una
              nueva receta para esta sucursal.
            </p>
          </div>
        ) : (
          /* =====================================================
              🔹 Listado (tabla en escritorio, tarjetas en móvil)
          ===================================================== */

          <>
            <RecipeTable
              recipes={items}
              onEdit={(item) => recipes.edit(item)}
              onDeactivate={(item) =>
                recipes.deactivate(item, request, previousPage)
              }
            />

            <div className="grid grid-cols-1 gap-4 p-5 sm:px-7 md:hidden">
              {items.map((item) => (
                <RecipeCard
                  key={item.id}
                  recipe={item}
                  onEdit={() => recipes.edit(item)}
                  onDeactivate={() =>
                    recipes.deactivate(item, request, previousPage)
                  }
                />
              ))}
            </div>
          </>
        )}

        {/* =====================================================
            🔹 Paginación
        ===================================================== */}

        <div className="border-t border-stroke px-5 pb-5 pt-4 dark:border-strokedark sm:px-7">
          <BasicPagination
            currentPage={recipes.page?.page.number ?? 0}
            totalPages={recipes.page?.page.totalPages ?? 0}
            totalElements={recipes.page?.page.totalElements ?? 0}
            size={recipes.page?.page.size ?? size}
            first={recipes.page ? recipes.page.page.number === 0 : true}
            last={recipes.page ? recipes.page.page.number >= recipes.page.page.totalPages - 1 : true}
            onPageChange={setPage}
            onSizeChange={(value) => {
              setSize(value);
              setPage(0);
            }}
          />
        </div>
      </SectionWrapper>

      {/* =====================================================
          🔹 Modal crear/editar
      ===================================================== */}

      <RestaurantRecipeFormModal
        isOpen={recipes.modalOpen}
        branchId={branchId}
        form={recipes.form}
        menuItems={recipes.menuItems}
        rawMaterials={recipes.rawMaterials}
        units={recipes.units}
        conversions={recipes.conversions}
        saving={recipes.saving}
        loadingOptions={recipes.loadingOptions}
        onClose={recipes.closeModal}
        onChange={recipes.updateField}
        onIngredientChange={recipes.updateIngredient}
        onAddIngredient={recipes.addIngredient}
        onRemoveIngredient={recipes.removeIngredient}
        onMoveIngredient={recipes.moveIngredient}
        onCreateMenuItem={recipes.createMenuItemInline}
        onSave={() =>
          recipes.save(request, previousPage)
        }
      />
    </WorkspaceLayout>
  );
};

export default RestaurantRecipesScreen;
